import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Block, Check, Undo } from '@mui/icons-material';

import {
  IArnoMappingRef,
  useArnoMappingDetail,
  useResetArnoMapping,
  useSetArnoMappingDecision,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import {
  decisionBlockReason,
  isHumanStatus,
  mappingStatusChip,
  SOURCE_TYPE_LABEL,
} from './arnoAdminLabels';
import { SelectorPicker } from './SelectorPicker';
import { TechnicalDetails } from './TechnicalDetails';
import { useAlert } from '../../context/AlertContext';

/**
 * Eine einzelne Zuordnung entscheiden (#1245).
 *
 * Der Dialog zeigt erst, wo die Variable überhaupt vorkommt — Vorkommen,
 * Bausteine, alte Namen — und dann die Auswahl. Ohne diese Herkunft wäre die
 * Entscheidung geraten, und geraten wird hier nichts.
 */

interface Props {
  mapping: IArnoMappingRef | null;
  onClose: () => void;
}

export const MappingDecisionDialog: React.FC<Props> = ({
  mapping,
  onClose,
}) => {
  const { showAlert } = useAlert();
  const { mappingDetail, isFetchingMappingDetail } =
    useArnoMappingDetail(mapping);
  const { setMappingDecision, isSettingMappingDecision } =
    useSetArnoMappingDecision();
  const { resetMapping, isResettingMapping } = useResetArnoMapping();

  const [targetPath, setTargetPath] = useState('');
  const [note, setNote] = useState('');

  // Beim Öffnen den bestehenden Stand übernehmen — der Dialog fängt nicht leer an.
  useEffect(() => {
    setTargetPath(mappingDetail?.targetPath ?? '');
    setNote(mappingDetail?.note ?? '');
  }, [mappingDetail]);

  const busy = isSettingMappingDecision || isResettingMapping;
  const blockReason = decisionBlockReason('CONFIRMED', targetPath);

  const save = async (status: 'CONFIRMED' | 'IGNORED') => {
    if (!mapping) {
      return;
    }
    try {
      await setMappingDecision({
        ...mapping,
        status,
        targetPath: status === 'IGNORED' ? null : targetPath.trim(),
        note: note.trim() || null,
      });
      showAlert({
        severity: 'success',
        text:
          status === 'IGNORED'
            ? `${mapping.sourceKey} bleibt bewusst ohne Ziel.`
            : `${mapping.sourceKey} ist auf ${targetPath.trim()} zugeordnet.`,
      });
      onClose();
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Speichern fehlgeschlagen: ${error.message}`
            : 'Speichern fehlgeschlagen.',
      });
    }
  };

  const reset = async () => {
    if (!mapping) {
      return;
    }
    try {
      await resetMapping(mapping);
      showAlert({
        severity: 'success',
        text: `${mapping.sourceKey} ist wieder offen — der nächste Import darf wieder vorschlagen.`,
      });
      onClose();
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Zurücksetzen fehlgeschlagen: ${error.message}`
            : 'Zurücksetzen fehlgeschlagen.',
      });
    }
  };

  const chip = mappingDetail ? mappingStatusChip(mappingDetail.status) : null;

  return (
    <Dialog
      open={Boolean(mapping)}
      onClose={busy ? undefined : onClose}
      maxWidth='md'
      fullWidth
    >
      <DialogTitle>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='h6' sx={{ flexGrow: 1 }}>
            {mapping?.sourceKey}
          </Typography>
          {chip && <Chip size='small' color={chip.color} label={chip.label} />}
        </Stack>
        <Typography variant='caption' color='text.secondary'>
          {mapping ? SOURCE_TYPE_LABEL[mapping.sourceType] : ''}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {isFetchingMappingDetail && !mappingDetail ? (
          <Stack alignItems='center' sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={2}>
            {mappingDetail && isHumanStatus(mappingDetail.status) && (
              <Alert severity='info'>
                Diese Zuordnung wurde von Hand entschieden. Kein Import
                überschreibt sie — erst „Zurücksetzen" gibt den automatischen
                Vorschlag wieder frei.
              </Alert>
            )}

            <Box>
              <Typography variant='subtitle2'>Quelle</Typography>
              <Typography variant='body2' color='text.secondary'>
                {mapping ? SOURCE_TYPE_LABEL[mapping.sourceType] : ''} ·{' '}
                {mapping?.sourceKey}
                {mappingDetail?.usage.legacyNames.length
                  ? ` · in ArnoTop auch: ${mappingDetail.usage.legacyNames.join(', ')}`
                  : ''}
              </Typography>
            </Box>

            <Box>
              <Typography variant='subtitle2'>Verwendung</Typography>
              <Typography variant='body2' color='text.secondary'>
                {mappingDetail?.usage.occurrences ?? 0} Vorkommen in{' '}
                {mappingDetail?.usage.blockCount ?? 0} Bausteinen
                {mappingDetail?.usage.family
                  ? ` · Familie ${mappingDetail.usage.family}`
                  : ''}
              </Typography>
              <Stack
                direction='row'
                spacing={0.5}
                sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}
              >
                {(mappingDetail?.usage.blocks ?? []).slice(0, 12).map(block => (
                  <Chip
                    key={block}
                    size='small'
                    variant='outlined'
                    label={block}
                  />
                ))}
                {(mappingDetail?.usage.blockCount ?? 0) > 12 && (
                  <Chip
                    size='small'
                    label={`+${(mappingDetail?.usage.blockCount ?? 0) - 12} weitere`}
                  />
                )}
              </Stack>
            </Box>

            {mappingDetail?.suggestedBy && (
              <Box>
                <Typography variant='subtitle2'>
                  Automatischer Vorschlag
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {mappingDetail.targetPath ?? 'ohne Ziel'} · ermittelt durch{' '}
                  {mappingDetail.suggestedBy}
                  {mappingDetail.confidence !== null
                    ? ` · Sicherheit ${Math.round(mappingDetail.confidence * 100)} %`
                    : ''}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Ein Vorschlag wird nie von allein bestätigt — erst mit dem
                  Knopf unten wird er eine Entscheidung.
                </Typography>
              </Box>
            )}

            <Divider />

            <SelectorPicker
              value={targetPath}
              onChange={setTargetPath}
              disabled={busy}
            />

            <TextField
              size='small'
              label='Notiz (warum diese Entscheidung?)'
              value={note}
              onChange={event => setNote(event.target.value)}
              disabled={busy}
              multiline
              minRows={2}
            />

            <TechnicalDetails
              entries={[
                { label: 'sourceType', value: mappingDetail?.sourceType },
                { label: 'sourceKey', value: mappingDetail?.sourceKey },
                { label: 'scope', value: mappingDetail?.scope },
                { label: 'status', value: mappingDetail?.status },
                { label: 'targetSystem', value: mappingDetail?.targetSystem },
                { label: 'transform', value: mappingDetail?.transform },
                { label: 'suggestedBy', value: mappingDetail?.suggestedBy },
                {
                  label: 'confidence',
                  value:
                    mappingDetail?.confidence !== null &&
                    mappingDetail?.confidence !== undefined
                      ? String(mappingDetail.confidence)
                      : null,
                },
                {
                  label: 'isHuman',
                  value: mappingDetail ? String(mappingDetail.isHuman) : null,
                },
                {
                  label: 'context',
                  value: mappingDetail?.context
                    ? JSON.stringify(mappingDetail.context)
                    : null,
                },
              ]}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Tooltip
          title={
            mappingDetail && isHumanStatus(mappingDetail.status)
              ? 'Entscheidung zurücknehmen — danach gilt wieder der automatische Vorschlag.'
              : 'Es gibt keine menschliche Entscheidung, die zurückgenommen werden könnte.'
          }
        >
          <span>
            <Button
              startIcon={<Undo />}
              onClick={reset}
              disabled={
                busy || !mappingDetail || !isHumanStatus(mappingDetail.status)
              }
            >
              Zurücksetzen
            </Button>
          </span>
        </Tooltip>

        <Stack direction='row' spacing={1}>
          <Button onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            startIcon={<Block />}
            onClick={() => save('IGNORED')}
            disabled={busy}
          >
            Bewusst ohne Ziel
          </Button>
          <Tooltip title={blockReason ?? ''}>
            <span>
              <Button
                variant='contained'
                startIcon={busy ? <CircularProgress size={16} /> : <Check />}
                onClick={() => save('CONFIRMED')}
                disabled={busy || Boolean(blockReason)}
              >
                Zuordnung bestätigen
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
